import { Api } from './Api';
import { ApiActor } from './ApiActor';
import { App } from '../app/App';
import { GetEndpointMethodNames } from './GetEndpointMethodNames';
import { useRecoilState } from 'recoil';
import React, { useContext } from 'react';

export type GetUseApiAtom<TApi extends Api<any, any>> = <
  TPath extends TApi['Path'],
  TMethod extends GetEndpointMethodNames<TApi, TPath>,
>(
  path: TPath,
  method: TMethod,
) => ApiActor<TApi, TPath, TMethod>;

export const createUseApiAtom = <TApp extends App<any, any>>(app: TApp) =>
  function useApiAtom<
    TPath extends TApp['_Api']['Path'],
    TMethod extends GetEndpointMethodNames<TApp['_Api'], TPath>,
  >(
    path: TPath,
    method: TMethod,
    key?: string,
  ): ApiActor<TApp['_Api'], TPath, TMethod> {
    const { getAtom, createFetch } = useContext(app.apiContext);
    const atom = getAtom(path, method, key);
    const [content, setContent] = useRecoilState(atom);

    const fetch = React.useMemo<
      ApiActor<TApp['_Api'], TPath, TMethod>['fetch']
    >(
      () => createFetch(path, method, { content, setContent }),
      [createFetch, method, path, content, setContent],
    );

    return {
      ...content,
      fetch,
    };
  };
